/**
 * Typography classes from the design system
 * See docs/design/typography.md for usage guidelines
 */
export const typography = {
  /** Page titles - 24px semibold */
  h1: "text-h1 font-semibold leading-snug text-text-theme-primary",
  /** Section headers - 18px semibold */
  h2: "text-h2 font-semibold leading-snug text-text-theme-primary",
  /** Subsection headers - 16px semibold */
  h3: "text-h3 font-semibold leading-tight text-text-theme-primary",
  /** Primary body text - 15px regular */
  body: "text-body font-normal leading-tight text-text-theme-primary",
  /** Secondary body text - 14px regular */
  bodySm: "text-body-sm font-normal leading-tight text-text-theme-secondary",
  /** Muted body text - 14px muted color */
  bodyMuted: "text-body-sm font-normal leading-tight text-text-theme-muted",
  /** Form labels - 14px medium */
  label: "text-label-md font-medium text-text-theme-secondary",
  /** Small muted text - 12px muted */
  smallMuted: "text-label-sm font-normal text-text-theme-muted",
  /** Micro text - 10px */
  micro: "text-label-xs font-normal text-text-theme-muted",
  /** Button text - 14px semibold */
  button: "text-label-md font-semibold",
} as const;

// buttonVariants has moved to @markeroo/theme
// Import from there: import { buttonVariants } from "@markeroo/theme";

export const pageContainer = "mx-auto flex w-full max-w-page flex-col gap-6 px-6 pb-16 pt-8 lg:px-8";
export const cardContainer = "flex flex-col gap-4 rounded-2xl bg-surface-card p-6 shadow-card";
/** @deprecated Use typography.bodyMuted instead */
export const mutedText = "text-body-sm text-text-theme-muted";
/** @deprecated Use typography.smallMuted instead */
export const smallMutedText = "text-label-sm text-text-theme-muted";
export const planPillClass = "inline-flex items-center rounded-full bg-semantic-plan-bg px-3 py-1 text-label-md font-semibold text-semantic-plan";
/** @deprecated Use typography.label instead */
export const fieldLabelClass = "flex flex-col gap-2 text-label-md font-medium text-brand-900";
// Form element variants have moved to @markeroo/theme
// Import from there: import { inputVariants, selectVariants, textareaVariants } from "@markeroo/theme";
// Or use the Select component: import { Select } from "@/components/Select";
/** @deprecated Use inputVariants.default from @markeroo/theme instead */
export const inputClass =
  "w-full rounded-lg border border-border bg-surface-card px-3 py-2 text-body text-brand-950 shadow-sm transition focus-visible:border-brand-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600/40 disabled:cursor-not-allowed disabled:opacity-60";
/** @deprecated Use selectVariants.default from @markeroo/theme or Select component instead */
export const selectClass = `${inputClass} pr-10 cursor-pointer appearance-none bg-[length:20px_20px] bg-[right_0.75rem_center] bg-no-repeat [background-image:url("data:image/svg+xml,%3Csvg%20xmlns%3D%27http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%27%20fill%3D%27none%27%20viewBox%3D%270%200%2024%2024%27%3E%3Cpath%20stroke%3D%27%236b7280%27%20stroke-linecap%3D%27round%27%20stroke-linejoin%3D%27round%27%20stroke-width%3D%272%27%20d%3D%27M8%2010.14a20.4%2020.4%200%200%200%203.702%203.893c.175.141.42.141.596%200A20.4%2020.4%200%200%200%2016%2010.14%27%2F%3E%3C%2Fsvg%3E")]`;
/** @deprecated Use textareaVariants.default from @markeroo/theme instead */
export const textareaClass = `${inputClass} resize-y`;
export const skeletonBarClass =
  "h-2 w-full animate-shimmer rounded-full bg-gradient-to-r from-border/60 via-border to-border/60 bg-[length:200px_100%]";

// Z-index scale for consistent layering
export const zIndex = {
  base: 1,
  dropdown: 10,
  sticky: 20,
  fixed: 30, // CompactUsageBox
  overlay: 40, // Modal backdrops
  modal: 1000, // Modal content
  toast: 1001, // Notifications, FeedbackButton
  tutorialSpotlight: 1001, // Tutorial spotlight overlay (above modals at 1000)
  tooltip: 2000, // Tooltips (above modals so they work inside modals)
  tutorialTooltip: 10001, // Tutorial instructions (topmost)
  tutorialRipple: 10002, // Tutorial ripple effects (above tooltip)
} as const;
